import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { AdminUser } from '@/types'

interface AuthState {
  user: AdminUser | null
  token: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
}

interface AuthStore extends AuthState {
  setAuth: (user: AdminUser, token: string, refreshToken?: string) => void
  setTokens: (accessToken: string, refreshToken: string) => void
  logout: () => void
  setLoading: (loading: boolean) => void
  hasPermission: (permission: string) => boolean
  hasRole: (roles: string[]) => boolean
}

const initialState: AuthState = {
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: true,
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      setAuth: (user, token, refreshToken) =>
        set({
          user,
          token,
          refreshToken: refreshToken ?? get().refreshToken,
          isAuthenticated: true,
          isLoading: false,
        }),

      setTokens: (accessToken, refreshToken) =>
        set({
          token: accessToken,
          refreshToken,
        }),

      logout: () =>
        set({
          ...initialState,
          isLoading: false,
        }),

      setLoading: (loading) => set({ isLoading: loading }),

      hasPermission: (permission: string) => {
        const { user } = get()
        if (!user) return false
        if (user.role === 'super_admin') return true
        return user.permissions.includes(permission) || user.permissions.includes('*')
      },

      hasRole: (roles: string[]) => {
        const { user } = get()
        if (!user) return false
        return roles.includes(user.role)
      },
    }),
    {
      name: 'admin-auth-storage',
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)
